import { useEffect, useId, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocation } from 'react-router-dom'
import {
  LOCATION_MODAL,
  type LocationSuggestion,
} from '../constants/location'
import { isCheckoutRoute } from '../helpers/checkout'
import { deliveryLocationFromSuggestion } from '../helpers/location'
import { useDetectDeliveryCity } from '../hooks/useDetectDeliveryCity'
import { useDeliveryLocation } from '../hooks/useDeliveryLocation'
import { useLocationSearch } from '../hooks/useLocationSearch'
import '../styles/location-modal.scss'

export function LocationModal() {
  const { pathname } = useLocation()
  const { location, isModalOpen, locationLocked, closeModal, selectLocation } =
    useDeliveryLocation()
  const locked = locationLocked || isCheckoutRoute(pathname)
  const { detect, isDetecting, error, clearError } = useDetectDeliveryCity()
  const [query, setQuery] = useState('')
  const { suggestions, isSearching, searchError } = useLocationSearch(
    query,
    isModalOpen,
  )
  const inputId = useId()

  useEffect(() => {
    if (locked && isModalOpen) closeModal()
  }, [locked, isModalOpen, closeModal])

  useEffect(() => {
    if (!isModalOpen) return
    setQuery(location?.label ?? '')
    clearError()
  }, [isModalOpen, location, clearError])

  if (locked || !isModalOpen) return null

  function onQueryChange(value: string) {
    clearError()
    setQuery(value)
  }

  function onPickSuggestion(suggestion: LocationSuggestion) {
    selectLocation(deliveryLocationFromSuggestion(suggestion))
  }

  async function onUseCurrent() {
    const detected = await detect()
    if (!detected) return
    selectLocation(detected)
  }

  const showNoResults =
    query.trim().length >= 2 &&
    !isSearching &&
    !searchError &&
    suggestions.length === 0

  return createPortal(
    <div
      className="location-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="location-modal-title"
      aria-describedby="location-modal-subtitle"
    >
      <button
        type="button"
        className="location-modal__backdrop"
        aria-label={LOCATION_MODAL.closeAria}
        onClick={closeModal}
      />

      <div className="location-modal__panel">
        <button
          type="button"
          className="location-modal__close"
          aria-label={LOCATION_MODAL.closeAria}
          onClick={closeModal}
        >
          ×
        </button>

        <div className="location-modal__hero">
          <div className="location-modal__hero-copy">
            <h2 id="location-modal-title">{LOCATION_MODAL.title}</h2>
            <p id="location-modal-subtitle">{LOCATION_MODAL.subtitle}</p>
          </div>
          <div className="location-modal__hero-art" aria-hidden>
            <RouteArt />
          </div>
        </div>

        <div className="location-modal__body">
          <div className="location-modal__field">
            <div className="location-modal__search">
              <label className="location-modal__search-label" htmlFor={inputId}>
                {LOCATION_MODAL.searchLabel}
              </label>
              <input
                id={inputId}
                type="text"
                autoComplete="street-address"
                placeholder={LOCATION_MODAL.searchPlaceholder}
                value={query}
                onChange={(event) => onQueryChange(event.target.value)}
              />
              {query ? (
                <button
                  type="button"
                  className="location-modal__clear"
                  aria-label={LOCATION_MODAL.clearSearch}
                  onClick={() => onQueryChange('')}
                >
                  ×
                </button>
              ) : null}
            </div>
          </div>

          {isSearching ? (
            <p className="location-modal__hint">{LOCATION_MODAL.searching}</p>
          ) : null}

          {suggestions.length > 0 ? (
            <ul className="location-modal__suggestions" role="listbox">
              {suggestions.map((suggestion) => (
                <li key={`${suggestion.city}-${suggestion.label}-${suggestion.lat}`}>
                  <button
                    type="button"
                    role="option"
                    className="location-modal__suggestion"
                    onClick={() => onPickSuggestion(suggestion)}
                  >
                    <PinIcon />
                    <span>
                      <strong>{suggestion.label}</strong>
                      {suggestion.city &&
                      !suggestion.label
                        .toLowerCase()
                        .includes(suggestion.city.toLowerCase()) ? (
                        <small>{suggestion.city}</small>
                      ) : null}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {showNoResults ? (
            <p className="location-modal__hint">{LOCATION_MODAL.noResults}</p>
          ) : null}

          {searchError && !isSearching ? (
            <p className="location-modal__error" role="alert">
              {searchError}
            </p>
          ) : null}

          <button
            type="button"
            className="location-modal__current"
            disabled={isDetecting}
            onClick={() => {
              void onUseCurrent()
            }}
          >
            <LocateIcon />
            <span>
              {isDetecting ? LOCATION_MODAL.detecting : LOCATION_MODAL.useCurrent}
            </span>
          </button>

          {error ? (
            <p className="location-modal__error" role="alert">
              {error}
            </p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** Folded map + pin, inspired by common location-map icons. */
function RouteArt() {
  return (
    <svg width="112" height="78" viewBox="0 0 112 78" fill="none" aria-hidden>
      {/* Folded map panels */}
      <path
        d="M8 28 L28 18 L28 58 L8 68 Z"
        stroke="var(--brand-green)"
        strokeWidth="2.4"
        strokeLinejoin="round"
        fill="var(--brand-green-soft)"
      />
      <path
        d="M28 18 L56 26 L56 66 L28 58 Z"
        stroke="var(--brand-green)"
        strokeWidth="2.4"
        strokeLinejoin="round"
        fill="#ffffff"
      />
      <path
        d="M56 26 L96 16 L96 56 L56 66 Z"
        stroke="var(--brand-green)"
        strokeWidth="2.4"
        strokeLinejoin="round"
        fill="var(--brand-green-soft)"
      />
      {/* Route across the map */}
      <path
        d="M18 46 C34 34, 48 52, 66 40 C78 32, 86 38, 92 36"
        stroke="var(--brand-gold)"
        strokeWidth="2.2"
        strokeDasharray="3.5 4.5"
        strokeLinecap="round"
        fill="none"
      />
      {/* Location pin on left panel */}
      <path
        d="M22 14c0-6.075 4.701-11 10.5-11S43 7.925 43 14c0 7.5-10.5 16.5-10.5 16.5S22 21.5 22 14Z"
        fill="var(--brand-green)"
      />
      <circle cx="32.5" cy="13.5" r="3.6" fill="#ffffff" />
    </svg>
  )
}

function PinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 21s7-5.2 7-11a7 7 0 1 0-14 0c0 5.8 7 11 7 11Z" />
      <circle cx="12" cy="10" r="2.4" />
    </svg>
  )
}

function LocateIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22" />
      <circle cx="12" cy="12" r="7.5" />
    </svg>
  )
}
